"use client";

import { RefObject, useEffect, useState } from 'react';
import { useLivingCards } from '@/context/livingCards';

interface InteractionState {
  isHovered: boolean;
  isFocused: boolean;
  isInteracting: boolean;
}

/**
 * Hook to manage interactions with a card element
 * Handles mouse, touch, and keyboard interactions
 */
export function useLivingCardInteraction(
  elementRef: RefObject<HTMLElement | null>,
  id: string
): InteractionState {
  const { registerHover } = useLivingCards();
  const [state, setState] = useState<InteractionState>({ 
    isHovered: false, 
    isFocused: false,
    isInteracting: false
  });
  
  // Skip adding event listeners during SSR or when ID is not set
  useEffect(() => {
    // Only add event listeners on the client side
    if (typeof window === 'undefined' || !id) return;
    
    const element = elementRef.current;
    if (!element) return;
    
    // Mouse event handlers
    const handleMouseEnter = () => {
      setState(s => ({ ...s, isHovered: true, isInteracting: true }));
      registerHover(id);
      
      // Apply classes for consistent styling
      // Note: border-accent class is applied via isInteracting in SpotlightLivingCard
      element.classList.add('touch-hover-active');
      element.classList.add('fake-hover');
    };
    
    const handleMouseLeave = () => {
      setState(s => ({ 
        ...s, 
        isHovered: false, 
        isInteracting: s.isFocused // Only still interacting if focused
      }));
      
      // Keep hover active if still focused
      if (!state.isFocused) {
        registerHover(null);
        element.classList.remove('touch-hover-active');
        element.classList.remove('fake-hover');
      }
    };
    
    // Touch event handlers
    const handleTouchStart = () => {
      setState(s => ({ ...s, isHovered: true, isInteracting: true }));
      registerHover(id);
      
      // Apply classes for consistent styling
      // Note: border-accent class is applied via isInteracting in SpotlightLivingCard
      element.classList.add('touch-hover-active');
      element.classList.add('fake-hover');
    };
    
    const handleTouchEnd = () => {
      setState(s => ({ 
        ...s, 
        isHovered: false, 
        isInteracting: s.isFocused // Only still interacting if focused
      }));
      
      // Only remove hover if not focused
      if (!state.isFocused) {
        registerHover(null);
        element.classList.remove('touch-hover-active');
        element.classList.remove('fake-hover');
      }
    };
    
    // Keyboard/focus event handlers
    const handleFocus = () => {
      setState(s => ({ ...s, isFocused: true, isInteracting: true }));
      registerHover(id);
      
      // Apply classes for consistent styling
      // Note: border-accent class is applied via isInteracting in SpotlightLivingCard
      element.classList.add('touch-hover-active');
      element.classList.add('fake-hover');
    };
    
    const handleBlur = () => {
      setState(s => ({ 
        ...s, 
        isFocused: false, 
        isInteracting: s.isHovered // Only still interacting if hovered
      }));
      
      // Only remove hover if not hovered
      if (!state.isHovered) {
        registerHover(null);
        element.classList.remove('touch-hover-active');
        element.classList.remove('fake-hover');
      }
    };
    
    // Add all event listeners
    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('mouseleave', handleMouseLeave);
    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchend', handleTouchEnd);
    element.addEventListener('focus', handleFocus);
    element.addEventListener('blur', handleBlur);
    
    // Cleanup function
    return () => {
      element.removeEventListener('mouseenter', handleMouseEnter);
      element.removeEventListener('mouseleave', handleMouseLeave);
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchend', handleTouchEnd);
      element.removeEventListener('focus', handleFocus);
      element.removeEventListener('blur', handleBlur);
    };
  }, [elementRef, id, registerHover, state.isFocused, state.isHovered]);
  
  return state;
}